import { useRef, useEffect, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { format, parseISO } from 'date-fns';
import { FileText, Pencil, Eye, Lock, Save } from 'lucide-react';
import { useDocsStore } from '../../stores/docsStore';
import { createMarkdownComponents } from '../../utils/markdownConfig';
import { Spinner } from '../common/Spinner';
import { EmptyState } from '../common/EmptyState';
import { DocsBreadcrumb } from './DocsBreadcrumb';
import { FileEditor } from './FileEditor';

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function FileViewer() {
  const {
    selectedPath, fileContent, contentLoading,
    editMode, setEditMode, isDirty, saving, saveFile,
    navigateToPath,
  } = useDocsStore();
  const scrollRef = useRef<HTMLDivElement>(null);

  // Scroll to top when file changes
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }
  }, [selectedPath]);

  const markdownComponents = useMemo(
    () => createMarkdownComponents(navigateToPath, fileContent?.path),
    [navigateToPath, fileContent?.path],
  );

  if (!selectedPath) {
    return (
      <div className="flex items-center justify-center h-full">
        <EmptyState
          icon={<FileText />}
          title="Select a file"
          description="Choose a file from the tree to view or edit its contents."
        />
      </div>
    );
  }

  if (contentLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!fileContent) {
    return (
      <div className="flex items-center justify-center h-full">
        <EmptyState
          icon={<FileText />}
          title="File not found"
          description="The file could not be loaded. It may be too large or access is denied."
        />
      </div>
    );
  }

  const lastModified = fileContent.lastModified
    ? format(parseISO(fileContent.lastModified), 'MMM d, yyyy h:mm a')
    : '';

  return (
    <div className="flex flex-col h-full">
      {/* Header bar */}
      <div className="flex-shrink-0 px-4 py-2.5 border-b border-border-subtle bg-bg-secondary">
        <div className="flex items-center justify-between gap-3">
          <DocsBreadcrumb path={fileContent.path} />
          <div className="flex items-center gap-2 flex-shrink-0">
            {fileContent.readOnly ? (
              <span className="flex items-center gap-1 text-[10px] text-text-muted bg-bg-tertiary px-2 py-0.5 rounded">
                <Lock className="h-3 w-3" />
                Read-only
              </span>
            ) : (
              <>
                <button
                  onClick={() => setEditMode(!editMode)}
                  className={`flex items-center gap-1 text-[10px] px-2 py-1 rounded transition-colors ${
                    editMode
                      ? 'bg-accent-purple/20 text-accent-purple-light'
                      : 'bg-bg-tertiary text-text-secondary hover:text-text-primary'
                  }`}
                >
                  {editMode ? <Eye className="h-3 w-3" /> : <Pencil className="h-3 w-3" />}
                  {editMode ? 'View' : 'Edit'}
                </button>
                {isDirty && (
                  <button
                    onClick={saveFile}
                    disabled={saving}
                    className="flex items-center gap-1 text-[10px] px-2 py-1 rounded bg-success/20 text-success hover:bg-success/30 transition-colors disabled:opacity-50"
                  >
                    <Save className="h-3 w-3" />
                    {saving ? 'Saving...' : 'Save'}
                  </button>
                )}
              </>
            )}
          </div>
        </div>
        <div className="flex items-center gap-3 mt-1 text-[10px] text-text-muted">
          <span>{formatFileSize(fileContent.size)}</span>
          {lastModified && <span>Modified {lastModified}</span>}
        </div>
      </div>

      {/* Content area */}
      {editMode ? (
        <FileEditor />
      ) : (
        <div ref={scrollRef} className="flex-1 overflow-y-auto p-6">
          <div className="max-w-3xl">
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
              {fileContent.content}
            </ReactMarkdown>
          </div>
        </div>
      )}
    </div>
  );
}

export default FileViewer;
